import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  Check,
  Close,
  PhoneInTalk
} from '@mui/icons-material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import axios from 'utils/axios';
import useLocales from 'hooks/useLocales';
import useAuth from 'hooks/useAuth';
import { STATUS_STYLES } from './CallRequestCell';

export type ExplanatoryCallDetails = {
  userName?: string;
  userPhone?: string;
  requestType?: string;
  requestCategory?: string;
  userGender?: string;
  referenceNumber?: string;
  meetingTime?: string;
  meetingDate?: string;
  status?: string;
};

type CallRequestDetailsDrawerProps = {
  open: boolean;
  onClose: () => void;
  explanatoryCallId?: string | null;
  onStatusUpdated?: () => void;
};

const PATH_STEPS = ['CallRequest', 'InProgress', 'Resolved'] as const;

const getNextStatus = (currentStatus?: string) => {
  if (currentStatus === 'CallRequest') return 'InProgress';
  if (currentStatus === 'InProgress') return 'Resolved';
  return null;
};

const getPreviousStatus = (currentStatus?: string) => {
  if (currentStatus === 'Resolved') return 'InProgress';
  if (currentStatus === 'InProgress') return 'CallRequest';
  return null;
};

const getStatusStepIndex = (status?: string) => {
  if (status === 'Resolved') return 2;
  if (status === 'InProgress') return 1;
  if (status === 'CallRequest') return 0;
  return -1;
};

const formatMeetingTime = (meetingTime?: string) => {
  if (!meetingTime) return '—';
  const parsed = moment(meetingTime, ['HH:mm:ss', 'HH:mm'], true);
  if (parsed.isValid()) return parsed.locale('en').format('h:mm A');
  const fallback = moment(meetingTime);
  return fallback.isValid() ? fallback.locale('en').format('h:mm A') : meetingTime;
};

const InfoItem = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} color="text.primary">
      {value || '—'}
    </Typography>
  </Box>
);

export default function CallRequestDetailsDrawer({
  open,
  onClose,
  explanatoryCallId,
  onStatusUpdated
}: CallRequestDetailsDrawerProps) {
  const { translate } = useLocales();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [updatingAction, setUpdatingAction] = useState<'forward' | 'back' | null>(null);
  const [details, setDetails] = useState<ExplanatoryCallDetails | null>(null);
  const [error, setError] = useState(false);

  const canManageExplanatoryCall =
    user?.role === 'Admin' || user?.permission?.includes('Explanatory Call');

  const fetchDetails = useCallback(async () => {
    if (!explanatoryCallId) return;
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get('/ExplanatoryCall/GetExplanatoryCallById', {
        params: { explanatoryCallId }
      });
      setDetails(response?.data?.result || null);
    } catch (e) {
      setError(true);
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [explanatoryCallId]);

  useEffect(() => {
    if (!open || !explanatoryCallId) {
      setDetails(null);
      setError(false);
      return;
    }
    fetchDetails();
  }, [open, explanatoryCallId, fetchDetails]);

  const handleUpdateStatus = async (direction: 'forward' | 'back') => {
    if (!explanatoryCallId || !details?.status) return;

    const nextStatus =
      direction === 'forward' ? getNextStatus(details.status) : getPreviousStatus(details.status);
    if (!nextStatus) return;

    setUpdatingAction(direction);
    try {
      const response = await axios.put('/ExplanatoryCall/UpdateExplanatoryCallStatus', {
        explanatoryCallId,
        status: nextStatus
      });

      if (response?.data?.status === false) {
        const apiError =
          response?.data?.errors?.[0] ||
          response?.data?.message ||
          translate('Something went wrong');
        enqueueSnackbar(apiError, { variant: 'error' });
        return;
      }

      setDetails((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      enqueueSnackbar(translate('Status updated successfully'), { variant: 'success' });
      onStatusUpdated?.();
    } catch (e: any) {
      const apiError = e?.errors?.[0] || e?.message || translate('Something went wrong');
      enqueueSnackbar(apiError, { variant: 'error' });
    } finally {
      setUpdatingAction(null);
    }
  };

  const statusKey = details?.status || '';
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.CallRequest;
  const activeStep = getStatusStepIndex(statusKey);
  const avatarLetter = (details?.userName || '?').trim().charAt(0).toUpperCase();
  const isUpdating = Boolean(updatingAction);
  const isPrimaryDisabled =
    statusKey === 'Resolved' ||
    statusKey === 'Cancelled' ||
    isUpdating ||
    !getNextStatus(statusKey);
  const isBackDisabled =
    statusKey === 'CallRequest' ||
    statusKey === 'Cancelled' ||
    isUpdating ||
    !getPreviousStatus(statusKey);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 420 },
          boxSizing: 'border-box',
          p: 2.5
        }
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Typography variant="h6" fontWeight={700}>
          {translate('Call Request Details')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Stack>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" py={8}>
          <CircularProgress size={32} />
        </Stack>
      ) : error ? (
        <Typography color="error" textAlign="center" py={4}>
          {translate('Something went wrong')}
        </Typography>
      ) : details ? (
        <Stack spacing={2.5}>
          <Box
            sx={{
              bgcolor: '#F4F6F8',
              borderRadius: 2,
              p: 2
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
              <Avatar
                sx={{
                  bgcolor: '#212B36',
                  width: 44,
                  height: 44,
                  fontSize: 18,
                  fontWeight: 700
                }}
              >
                {avatarLetter}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {details.userName || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {details.userPhone || '—'}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5
              }}
            >
              <InfoItem label={translate('type')} value={details.requestType} />
              <InfoItem label={translate('category')} value={details.requestCategory} />
              <InfoItem label={translate('gender')} value={details.userGender} />
              <InfoItem label={translate('reference number')} value={details.referenceNumber} />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#FFF8E7',
              border: '1px solid #E6D5A8',
              borderRadius: 2,
              p: 2
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <PhoneInTalk sx={{ color: '#B8860B', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#B8860B">
                {translate('Call Request Details')}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 16, color: '#B8860B' }} />
                <Typography variant="body2" color="text.primary">
                  {formatMeetingTime(details.meetingTime)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarToday sx={{ fontSize: 16, color: '#B8860B' }} />
                <Typography variant="body2" color="text.primary">
                  {details.meetingDate}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>
              {translate('Request Path')}
            </Typography>

            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              {PATH_STEPS.map((step, index) => {
                const isDone = activeStep >= index;
                const isLast = index === PATH_STEPS.length - 1;

                return (
                  <Stack key={step} alignItems="center" sx={{ flex: 1, position: 'relative' }}>
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 14,
                          left: '50%',
                          width: '100%',
                          height: 2,
                          bgcolor: activeStep > index ? '#C9A84C' : '#E0E0E0',
                          zIndex: 0
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: isDone ? '#C9A84C' : '#F0F0F0',
                        color: isDone ? '#fff' : '#9E9E9E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        border: isDone ? 'none' : '2px solid #E0E0E0'
                      }}
                    >
                      {isDone ? (
                        <Check sx={{ fontSize: 16 }} />
                      ) : (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#BDBDBD'
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      textAlign="center"
                      mt={1}
                      fontWeight={600}
                      color={isDone ? 'text.primary' : 'text.secondary'}
                      sx={{ px: 0.5 }}
                    >
                      {translate(step)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <Divider />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              {translate('Current Status')}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 20,
                bgcolor: statusStyle.bg,
                border: `1px solid ${statusStyle.border}`,
                color: statusStyle.color
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: statusStyle.color
                }}
              />
              <Typography variant="caption" fontWeight={700} color="inherit">
                {translate(statusKey)}
              </Typography>
            </Box>
          </Stack>

          {canManageExplanatoryCall && (
            <Stack spacing={1.25} pt={1}>
              <Button
                fullWidth
                variant="outlined"
                disabled={isPrimaryDisabled}
                onClick={() => handleUpdateStatus('forward')}
                sx={{
                  py: 1.25,
                  borderRadius: 10,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#E8F8EF',
                  borderColor: '#5CB87A',
                  color: '#2E7D4F',
                  '&:hover': {
                    bgcolor: '#D8F0E3',
                    borderColor: '#5CB87A'
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#F4F6F8',
                    borderColor: '#C4CDD5',
                    color: '#919EAB'
                  }
                }}
              >
                {updatingAction === 'forward' ? (
                  <CircularProgress size={22} color="inherit" />
                ) : statusKey === 'CallRequest' ? (
                  translate('Mark as In Progress')
                ) : (
                  translate('Call Completed Marked as Resolved')
                )}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                disabled={isBackDisabled}
                onClick={() => handleUpdateStatus('back')}
                sx={{
                  py: 1.25,
                  borderRadius: 10,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#F4F6F8',
                  borderColor: '#C4CDD5',
                  color: '#637381',
                  '&:hover': {
                    bgcolor: '#ECEFF1',
                    borderColor: '#C4CDD5'
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#F4F6F8',
                    borderColor: '#C4CDD5',
                    color: '#919EAB'
                  }
                }}
              >
                {updatingAction === 'back' ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  translate('Returned to Waiting')
                )}
              </Button>
            </Stack>
          )}
        </Stack>
      ) : null}
    </Drawer>
  );
}
